import React, { FC, useEffect, useMemo, useState } from 'react'
import form from 'styles/components/form.module.scss'
import styles from 'styles/components/template.module.scss'
import text from 'styles/utility/text.module.scss'
import { Spacer } from 'components/Utility'
import { NavButton, ScrollTopButton } from 'components/Button'
import { DatePicker, SearchForm } from 'components/Form'
import { useString } from 'hooks/form'
import { Caution, SmallHeadline } from 'components/Typography'
import { BasketType } from 'types/basket'
import { OrderTime, OrderType } from 'types/checkout'
import { useSearchOrder, useQueryOrderedUsers, useSumOrderAmount } from 'hooks/order'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { getBaskets } from 'reducks/basket/selectors'
import { getOrders, getOrderTimes } from 'reducks/checkout/selectors'
import { useSkip } from 'hooks/effect'
import { fetchBasketGroup } from 'reducks/basket/operations'
import { countOrderTimes, fetchOrderGroup } from 'reducks/checkout/operations'
import { SearchOrderCardList } from 'components/Card'
import { useQueryAllStores } from 'hooks/store'
import { fetchStoreShippingGroup } from 'reducks/storeShipping/operations'
import { getStoreShippings } from 'reducks/storeShipping/selectors'
import { StoreShippingType } from 'types/storeShipping'
import { db } from 'firebase/index'

const OrderSearchTemplate: FC = () => {
  const dispatch = useDispatch()
  const baskets: BasketType[] = useSelector((state: State) => getBaskets(state))
  const orders = useSelector((state: State) => getOrders(state))
  const orderTimes: OrderTime[] = useSelector((state: State) => getOrderTimes(state))
  const storeShippings = useSelector((state: State) => getStoreShippings(state))

  const { state: startDate, handleState: handleStartDate } = useString<string>('')
  const { state: endDate, handleState: handleEndDate } = useString<string>('')
  const { state: keyword, handleState: handleKeyword, setState: setKeyword } = useString<string>('')
  const [basketList, setBasketList] = useState<BasketType[]>([])
  const [orderList, setOrderList] = useState<OrderType[]>([])
  const [storeShippingList, setStoreShippingList] = useState<StoreShippingType[]>([])

  const currentDate = useMemo(() => new Date(), [])
  const stores = useQueryAllStores()
  const storeRefs = useMemo(() => stores.map((store) => db.collection('stores').doc(store.store_id)), [stores])

  // 日付範囲を指定せずに検索した際、グローバルなbasketsは空だがローカルのbasketListが更新される
  const userIds = useMemo(() => {
    const userIdsFromBaskets =
      baskets.length > 0 ? baskets.map((basket) => basket.user_id) : basketList.map((basket) => basket.user_id)
    const userIdsFromStoreShippings =
      storeShippings.length > 0
        ? storeShippings.map((storeShipping) => storeShipping.user_id)
        : storeShippingList.map((storeShipping) => storeShipping.user_id)
    return [...userIdsFromBaskets, ...userIdsFromStoreShippings].reduce(
      (prev, userId) => (prev.includes(userId) ? prev : [...prev, userId]),
      []
    )
  }, [baskets, basketList, storeShippings, storeShippingList])

  const users = useQueryOrderedUsers(userIds)

  const searchOrder = useSearchOrder({
    baskets,
    orders,
    setBasketList,
    setOrderList,
    setStoreShippingList,
    storeShippings,
    stores,
    users,
  })

  const orderAmount = orderList.length

  // 受取番号を取得する
  useEffect(() => {
    if (startDate === '' || endDate === '') return
    dispatch(fetchBasketGroup(new Date(startDate), new Date(endDate)))
  }, [startDate, endDate])

  // 取り寄せ情報を再取得する
  useEffect(() => {
    if (startDate === '' || endDate === '') return
    dispatch(fetchStoreShippingGroup(new Date(startDate), new Date(endDate)))
  }, [startDate, endDate])

  // 注文情報を再取得する
  useEffect(() => {
    if (startDate === '' || endDate === '') return
    dispatch(fetchOrderGroup(new Date(startDate), new Date(endDate)))
  }, [startDate, endDate])

  // 注文回数を取得
  useEffect(() => {
    if (!currentDate || !storeRefs || !userIds || storeRefs.length === 0 || userIds.length === 0) return
    dispatch(countOrderTimes(userIds, currentDate, storeRefs))
  }, [userIds, currentDate, storeRefs])

  useEffect(() => {
    setBasketList([...baskets])
  }, [baskets])

  useEffect(() => {
    setOrderList([...orders])
  }, [orders])

  useEffect(() => {
    setStoreShippingList([...storeShippings])
  }, [storeShippings])

  // 検索フォームが空になったら検索結果をリセット(初回のレンダリングはスキップ)
  useSkip(
    () => {
      if (keyword !== '') return
      setBasketList([...baskets])
      setOrderList([...orders])
      setStoreShippingList([...storeShippings])
    },
    [keyword],
    1
  )

  return (
    <div className={styles.standard}>
      <Spacer size={20} />
      <SearchForm
        isFilled={keyword !== ''}
        onChange={handleKeyword}
        onClick={() => searchOrder(keyword)}
        placeholder={'注文番号/店名/注文者名で検索'}
        value={keyword}
      />
      <Spacer size={16} />
      <p className={text.font_small}>注文を取得する期間</p>
      <Spacer size={8} />
      <div className={form.column_two}>
        <div className={form.column_two__item}>
          <DatePicker onChange={handleStartDate} max={endDate} value={startDate} />
        </div>
        <Spacer size={8} />
        <div className={form.column_two__item}>
          <DatePicker onChange={handleEndDate} min={startDate} value={endDate} />
        </div>
      </div>
      <Spacer size={28} />
      <SmallHeadline label={`${orderAmount}件の注文を表示中`} />
      {orderAmount >= 100 && (
        <Caution caution={'※対象の注文が多すぎるため、表示を制限しています。検索範囲を狭めて再度お試しください。'} />
      )}
      <Spacer size={12} />
      <SearchOrderCardList
        baskets={basketList}
        orderTimes={orderTimes}
        orders={orderList}
        storeShippings={storeShippingList}
        stores={stores}
        users={users}
      />
      <Spacer size={48} />
      <NavButton label={'トップページに戻る'} iconPath={'/icon/grey/home.svg'} url={'/admin/shops'} />
      <Spacer size={72} />
      <ScrollTopButton />
      <Spacer size={20} />
    </div>
  )
}

export default OrderSearchTemplate
